import { validateRingId } from "@/components/activation/device_id_registrations";
import {
  REGISTRATION_METHOD_INPUT,
  useRingIdRegistrationAPI,
} from "@/components/activation/DeviceIdRegistrationAPI";
import { NonFieldError } from "@/components/common/SCAlert";
import { SCLink } from "@/components/common/SCLink";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import { TextForm } from "@/components/sleep_checkup_v1/TextForm";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// このページが受け取るstateの型
export type StateType = {
  sleepCheckupUuid: string;
};

export function RingIdInput() {
  const [ringId, setRingId] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const api = useRingIdRegistrationAPI();

  const { sleepCheckupUuid } = (location.state ?? {}) as StateType;

  const onRegisterDeviceIdHandler = useCallback(async () => {
    try {
      await api.post({
        ringId,
        sleepCheckupUuid,
        registrationMethod: REGISTRATION_METHOD_INPUT,
      });
      navigate("/examinee/registration_completed", {
        state: {
          sleepCheckupUuid: sleepCheckupUuid,
        },
      });
    } catch (e) {
      setErrorMessage(
        "エラーが発生しました。しばらく経ってから、再試行してください。"
      );
    }
  }, [api, ringId, sleepCheckupUuid, navigate]);

  const validator = () => validateRingId(ringId);
  const isInvalidRingId = validateRingId(ringId) != null;

  if (!sleepCheckupUuid) {
    return (
      <GeneralError
        to="/examinee/signup"
        buttonText="アカウント登録をやり直す"
      />
    );
  }

  return (
    <>
      <LinearProgress
        variant="determinate"
        value={(5 / 6) * 100}
        sx={{
          borderRadius: 1,
          mx: 4,
          my: 6,
          height: "6px",
        }}
      />
      <ScrollToTop />
      <Stack sx={{ px: 4 }}>
        <Stack sx={{ py: 6 }}>
          <Typography
            variant="h6"
            align="center"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: "170%",
              letterSpacing: "0.15px",
            }}
          >
            デバイスIDの登録
          </Typography>
          <Typography
            variant="subtitle2"
            align="center"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "190%",
              letterSpacing: "0.1px",
              color: "text.secondary",
            }}
          >
            （リング型デバイス）
          </Typography>
        </Stack>
        <Stack spacing={4} sx={{ pb: 6 }}>
          <NonFieldError>{errorMessage}</NonFieldError>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: "Noto Sans JP",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "190%",
              letterSpacing: "0.1px",
              textAlign: "justify",
              color: "text.secondary",
            }}
          >
            手入力によりデバイスIDの登録を行います。収納ケースの外装に表示されているデバイスID(英数字15桁)を入力してください。
          </Typography>
          <img
            src="/img/image_ring_id_input.png"
            alt="デバイスIDの記載場所"
            loading="lazy"
            style={{ width: "100%", height: "auto" }}
          />
        </Stack>
        <Stack sx={{ pb: 6 }}>
          <TextForm
            title="デバイスID"
            placeholder="例：C11E00000000000"
            validator={validator}
            onChange={(e) => setRingId(e.target.value.toUpperCase())}
            inputProps={{ style: { textTransform: "uppercase" } }}
          />
        </Stack>
        <Stack spacing={4} sx={{ pt: 6, pb: 16 }}>
          <Button
            variant="contained"
            size="large"
            onClick={onRegisterDeviceIdHandler}
            disabled={isInvalidRingId}
          >
            登録する
          </Button>
          <SCLink
            variant="body2"
            to={`/examinee/ring_id_scan_introduction/${sleepCheckupUuid}`}
            sx={{ color: "text.secondary" }}
          >
            QRコードの読み取りに戻る
          </SCLink>
        </Stack>
      </Stack>
    </>
  );
}
